import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Combine,
  Download,
  File as FileIcon,
  FileCode,
  FileSpreadsheet,
  FileText,
  Folder as FolderIcon,
  FolderOpen,
  Image as ImageIcon,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  Search,
  Trash2,
} from 'lucide-react';
import type { ProjectFile } from '../../types';
import { isCsvType, isHtmlType, isImageType, isPdfType } from '../../utils/projectFileService';
import {
  normalizeFolderPath,
  UNSORTED_FOLDER_PATH,
  writableProjectFolders,
  type ProjectFolderDef,
} from '../../utils/projectFileFolders';
import { canSeeProjectFinancials } from '../../utils/userRoles';
import ProjectFileCommentsButton from './ProjectFileCommentsButton';
import ProjectPdfMergeModal from './ProjectPdfMergeModal';
import { useAuth } from '../../utils/AuthContext';
import { portalHostedAssetHref } from '../../utils/portalAssetUrl';

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileTypeIcon = (mimeType: string, size = 18): React.ReactNode => {
  if (isImageType(mimeType)) return <ImageIcon size={size} />;
  if (isPdfType(mimeType)) return <FileText size={size} />;
  if (mimeType.includes('html')) return <FileCode size={size} />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv')
    return <FileSpreadsheet size={size} />;
  return <FileIcon size={size} />;
};

type SortKey = 'date' | 'name' | 'size' | 'uploader' | 'type';
type SortDir = 'asc' | 'desc';
type GroupKey = 'none' | 'uploader' | 'date' | 'type';
type ViewMode = 'list' | 'icon';

interface FilesViewPrefs {
  sortKey: SortKey;
  sortDir: SortDir;
  groupBy: GroupKey;
  view: ViewMode;
}

const DEFAULT_PREFS: FilesViewPrefs = {
  sortKey: 'date',
  sortDir: 'desc',
  groupBy: 'none',
  view: 'list',
};

const PREFS_KEY = (projectId: string) => `pf:${projectId}:filesView`;

function loadPrefs(projectId: string): FilesViewPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(PREFS_KEY(projectId));
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<FilesViewPrefs>;
    const groupRaw = parsed.groupBy;
    const groupBy: GroupKey =
      groupRaw === 'uploader' || groupRaw === 'date' || groupRaw === 'type' ? groupRaw : 'none';
    return {
      sortKey:
        parsed.sortKey === 'name' ||
        parsed.sortKey === 'size' ||
        parsed.sortKey === 'uploader' ||
        parsed.sortKey === 'type' ||
        parsed.sortKey === 'date'
          ? parsed.sortKey
          : DEFAULT_PREFS.sortKey,
      sortDir: parsed.sortDir === 'asc' ? 'asc' : 'desc',
      groupBy,
      view: parsed.view === 'icon' ? 'icon' : 'list',
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(projectId: string, prefs: FilesViewPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFS_KEY(projectId), JSON.stringify(prefs));
  } catch {
    /* ignore quota / disabled storage */
  }
}

/** Coarse mime category used for both group-by and sort-by 'type'. */
function typeCategory(mimeType: string): string {
  if (isImageType(mimeType)) return 'Images';
  if (isPdfType(mimeType)) return 'PDFs';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv')
    return 'Spreadsheets';
  if (mimeType.includes('html')) return 'HTML';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'Documents';
  if (mimeType.startsWith('video/')) return 'Video';
  if (mimeType.startsWith('audio/')) return 'Audio';
  return 'Other';
}

/** Bucket label for date grouping, anchored at the local-day start. */
function dateBucket(d: Date): string {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfWeek = startOfToday - now.getDay() * 24 * 60 * 60 * 1000;
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const t = d.getTime();
  if (t >= startOfToday) return 'Today';
  if (t >= startOfWeek) return 'This week';
  if (t >= startOfMonth) return 'This month';
  return 'Older';
}

const DATE_BUCKET_ORDER: Record<string, number> = {
  Today: 0,
  'This week': 1,
  'This month': 2,
  Older: 3,
};

interface RenderRowDeps {
  view: ViewMode;
  fileHasUnreadFileComment: (file: ProjectFile) => boolean;
  getFileCommentCount: (file: ProjectFile) => number;
  onOpenPdf: (file: ProjectFile) => void;
  onOpenImage: (file: ProjectFile) => void;
  onOpenCsv: (file: ProjectFile) => void;
  onOpenHtml: (file: ProjectFile) => void;
  onDownload: (file: ProjectFile) => void;
  onDelete: (file: ProjectFile) => void;
  deletingId: string | null;
  mergeSelectedIds: Set<string>;
  onToggleMergeSelect: (fileId: string) => void;
  showMergeSelect: boolean;
  /** When set, rows get a "Move to folder" select (Project folders view). */
  onMoveToFolder?: (file: ProjectFile, folderPath: string | null) => void;
  folderMoveOptions: ProjectFolderDef[];
  /** Hide delete (partner / read-only surfaces). */
  readOnly?: boolean;
}

export interface ProjectFilesUnversionedProps {
  projectId: string;
  dealId?: string;
  /** Should already be filtered to !streamId by parent. */
  files: ProjectFile[];
  fileHasUnreadFileComment: (file: ProjectFile) => boolean;
  getFileCommentCount: (file: ProjectFile) => number;
  onOpenPdf: (file: ProjectFile) => void;
  onOpenImage: (file: ProjectFile) => void;
  onOpenCsv: (file: ProjectFile) => void;
  onOpenHtml: (file: ProjectFile) => void;
  onDownload: (file: ProjectFile) => void;
  onDelete: (file: ProjectFile) => void;
  deletingId: string | null;
  /** Project folders view: lets staff file a document into a default folder. */
  onMoveToFolder?: (file: ProjectFile, folderPath: string | null) => void;
  /** Heading shown in the toolbar (default "Files"). */
  title?: string;
  readOnly?: boolean;
}

const ProjectFilesUnversioned: React.FC<ProjectFilesUnversionedProps> = ({
  projectId,
  dealId,
  files,
  fileHasUnreadFileComment,
  getFileCommentCount,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
  onOpenHtml,
  onDownload,
  onDelete,
  deletingId,
  onMoveToFolder,
  title = 'Files',
  readOnly = false,
}) => {
  const { user } = useAuth();
  const folderMoveOptions = useMemo(
    () => writableProjectFolders(canSeeProjectFinancials(user)),
    [user],
  );
  const [prefs, setPrefsState] = useState<FilesViewPrefs>(() => loadPrefs(projectId));
  const [mergeSelectedIds, setMergeSelectedIds] = useState<Set<string>>(() => new Set());
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  useEffect(() => {
    setPrefsState(loadPrefs(projectId));
  }, [projectId]);
  const setPrefs = useCallback(
    (next: FilesViewPrefs | ((prev: FilesViewPrefs) => FilesViewPrefs)) => {
      setPrefsState((prev) => {
        const value = typeof next === 'function' ? (next as (p: FilesViewPrefs) => FilesViewPrefs)(prev) : next;
        savePrefs(projectId, value);
        return value;
      });
    },
    [projectId],
  );

  const [search, setSearch] = useState('');
  const [drillKey, setDrillKey] = useState<string | null>(null);

  const pdfFiles = useMemo(
    () => files.filter(f => isPdfType(f.mimeType) && f.id),
    [files],
  );
  const showMergeSelect = pdfFiles.length >= 2;

  const toggleMergeSelect = useCallback((fileId: string) => {
    setMergeSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);

  const mergeSelectedFiles = useMemo(
    () => pdfFiles.filter(f => f.id && mergeSelectedIds.has(f.id)),
    [pdfFiles, mergeSelectedIds],
  );

  const searchedFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return files.filter((f) => {
      if (!q) return true;
      if (f.name.toLowerCase().includes(q)) return true;
      if ((f.uploadedByName || '').toLowerCase().includes(q)) return true;
      return false;
    });
  }, [files, search]);

  const sorted = useMemo(() => {
    const copy = [...searchedFiltered];
    const dir = prefs.sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      let cmp = 0;
      switch (prefs.sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'size':
          cmp = (a.size || 0) - (b.size || 0);
          break;
        case 'uploader':
          cmp = (a.uploadedByName || '').localeCompare(b.uploadedByName || '');
          break;
        case 'type':
          cmp = typeCategory(a.mimeType).localeCompare(typeCategory(b.mimeType));
          break;
        case 'date':
        default:
          cmp = a.uploadedAt.getTime() - b.uploadedAt.getTime();
          break;
      }
      if (cmp === 0) cmp = a.uploadedAt.getTime() - b.uploadedAt.getTime();
      return cmp * dir;
    });
    return copy;
  }, [searchedFiltered, prefs.sortKey, prefs.sortDir]);

  /** Either a single bucket (no grouping / drilled in / no group key) or a list of named buckets. */
  const grouped = useMemo<{ key: string; label: string; files: ProjectFile[] }[]>(() => {
    if (prefs.groupBy === 'none') {
      return [{ key: '__all__', label: 'All files', files: sorted }];
    }
    const buckets = new Map<string, { key: string; label: string; files: ProjectFile[] }>();
    for (const f of sorted) {
      let key = '';
      let label = '';
      switch (prefs.groupBy) {
        case 'uploader':
          label = f.uploadedByName?.trim() || 'Unknown';
          key = label.toLowerCase();
          break;
        case 'date':
          label = dateBucket(f.uploadedAt);
          key = label;
          break;
        case 'type':
          label = typeCategory(f.mimeType);
          key = label;
          break;
      }
      if (!buckets.has(key)) buckets.set(key, { key, label, files: [] });
      buckets.get(key)!.files.push(f);
    }
    const list = Array.from(buckets.values());
    if (prefs.groupBy === 'date') {
      list.sort((a, b) => (DATE_BUCKET_ORDER[a.label] ?? 99) - (DATE_BUCKET_ORDER[b.label] ?? 99));
    } else {
      list.sort((a, b) => a.label.localeCompare(b.label));
    }
    return list;
  }, [sorted, prefs.groupBy]);

  /** Drilled-in single group (when applicable). */
  const drilled = useMemo(() => {
    if (!drillKey || prefs.groupBy === 'none') return null;
    return grouped.find((g) => g.key === drillKey) || null;
  }, [drillKey, grouped, prefs.groupBy]);

  // Reset drill if the current group key vanishes (e.g. group-by changed or files removed).
  useEffect(() => {
    if (drillKey && (!grouped.some((g) => g.key === drillKey) || prefs.groupBy === 'none')) {
      setDrillKey(null);
    }
  }, [drillKey, grouped, prefs.groupBy]);

  const renderDeps: RenderRowDeps = {
    view: prefs.view,
    fileHasUnreadFileComment,
    getFileCommentCount,
    onOpenPdf,
    onOpenImage,
    onOpenCsv,
    onOpenHtml,
    onDownload,
    onDelete,
    deletingId,
    mergeSelectedIds,
    onToggleMergeSelect: toggleMergeSelect,
    showMergeSelect,
    onMoveToFolder,
    folderMoveOptions,
    readOnly,
  };

  if (files.length === 0) return null;

  return (
    <div className="pf-section pf-files-section">
      {mergeModalOpen && user?.id && mergeSelectedFiles.length >= 2 && (
        <ProjectPdfMergeModal
          projectId={projectId}
          dealId={dealId}
          files={mergeSelectedFiles}
          uploadedBy={user.id}
          uploadedByName={user.name || user.email || 'Unknown'}
          onClose={() => setMergeModalOpen(false)}
          onComplete={() => {
            setMergeSelectedIds(new Set());
            setMergeModalOpen(false);
          }}
        />
      )}
      <div className="pf-files-toolbar">
        <div className="pf-files-toolbar-left">
          <FolderOpen size={14} aria-hidden />
          <h3 className="pf-section-title pf-files-title">{title}</h3>
          <span className="pf-files-count">
            {files.length} item{files.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="pf-files-toolbar-right">
          {showMergeSelect && (
            <button
              type="button"
              className="pf-files-merge-btn"
              disabled={mergeSelectedFiles.length < 2}
              onClick={() => setMergeModalOpen(true)}
              title="Merge selected PDFs and preserve comments, dimensions, and take-offs"
            >
              <Combine size={14} aria-hidden />
              Merge PDFs{mergeSelectedFiles.length > 0 ? ` (${mergeSelectedFiles.length})` : ''}
            </button>
          )}
          <div className="pf-files-search">
            <Search size={13} aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or uploader…"
              aria-label="Search files"
            />
          </div>
          <label className="pf-files-toolbar-field">
            <span className="pf-files-toolbar-label">Sort</span>
            <select
              className="pf-files-toolbar-select"
              value={prefs.sortKey}
              onChange={(e) => setPrefs((p) => ({ ...p, sortKey: e.target.value as SortKey }))}
              aria-label="Sort files by"
            >
              <option value="date">Date uploaded</option>
              <option value="name">Name</option>
              <option value="size">Size</option>
              <option value="uploader">Uploader</option>
              <option value="type">Type</option>
            </select>
            <button
              type="button"
              className="pf-files-sort-dir"
              onClick={() => setPrefs((p) => ({ ...p, sortDir: p.sortDir === 'asc' ? 'desc' : 'asc' }))}
              title={prefs.sortDir === 'asc' ? 'Ascending' : 'Descending'}
              aria-label={`Sort direction: ${prefs.sortDir === 'asc' ? 'ascending' : 'descending'}`}
            >
              {prefs.sortDir === 'asc' ? '↑' : '↓'}
            </button>
          </label>
          <label className="pf-files-toolbar-field">
            <span className="pf-files-toolbar-label">Group</span>
            <select
              className="pf-files-toolbar-select"
              value={prefs.groupBy}
              onChange={(e) => {
                setDrillKey(null);
                setPrefs((p) => ({ ...p, groupBy: e.target.value as GroupKey }));
              }}
              aria-label="Group files by"
            >
              <option value="none">None</option>
              <option value="uploader">Uploader</option>
              <option value="date">Date</option>
              <option value="type">Type</option>
            </select>
          </label>
          <div className="pf-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`pf-view-toggle-btn${prefs.view === 'list' ? ' pf-view-toggle-btn--active' : ''}`}
              onClick={() => setPrefs((p) => ({ ...p, view: 'list' }))}
              aria-pressed={prefs.view === 'list'}
              title="List view"
            >
              <ListIcon size={14} />
            </button>
            <button
              type="button"
              className={`pf-view-toggle-btn${prefs.view === 'icon' ? ' pf-view-toggle-btn--active' : ''}`}
              onClick={() => setPrefs((p) => ({ ...p, view: 'icon' }))}
              aria-pressed={prefs.view === 'icon'}
              title="Icon view"
            >
              <LayoutGrid size={14} />
            </button>
          </div>
        </div>
      </div>

      {drilled && (
        <nav className="pf-files-crumbs" aria-label="Folder breadcrumb">
          <button type="button" className="pf-files-crumb-btn" onClick={() => setDrillKey(null)}>
            <ChevronLeft size={13} aria-hidden /> Files
          </button>
          <span className="pf-files-crumb-sep" aria-hidden>
            /
          </span>
          <span className="pf-files-crumb-current">{drilled.label}</span>
          <span className="pf-files-crumb-count">{drilled.files.length}</span>
        </nav>
      )}

      {drilled ? (
        <FilesGroupBody files={drilled.files} {...renderDeps} />
      ) : prefs.groupBy === 'none' ? (
        <FilesGroupBody files={grouped[0]?.files ?? []} {...renderDeps} />
      ) : (
        <div className="pf-folder-list">
          {grouped.map((g) => (
            <FolderGroupCard
              key={g.key}
              label={g.label}
              count={g.files.length}
              previewFiles={g.files.slice(0, 4)}
              onOpen={() => setDrillKey(g.key)}
            />
          ))}
        </div>
      )}

      {sorted.length === 0 && (
        <div className="pf-files-empty">{search ? 'No files match the current filters.' : 'No files yet.'}</div>
      )}
    </div>
  );
};

interface FolderGroupCardProps {
  label: string;
  count: number;
  previewFiles: ProjectFile[];
  onOpen: () => void;
}

const FolderGroupCard: React.FC<FolderGroupCardProps> = ({ label, count, previewFiles, onOpen }) => {
  return (
    <button type="button" className="pf-folder-card" onClick={onOpen}>
      <div className="pf-folder-card-icon" aria-hidden>
        <FolderIcon size={28} />
      </div>
      <div className="pf-folder-card-body">
        <div className="pf-folder-card-title">{label}</div>
        <div className="pf-folder-card-meta">
          {count} item{count !== 1 ? 's' : ''}
          {previewFiles.length > 0 ? ' · ' : ''}
          {previewFiles
            .map((f) => f.name)
            .slice(0, 2)
            .join(', ')}
          {previewFiles.length > 2 ? '…' : ''}
        </div>
      </div>
      <ChevronRight size={14} className="pf-folder-card-chevron" aria-hidden />
    </button>
  );
};

interface FilesGroupBodyProps extends RenderRowDeps {
  files: ProjectFile[];
}

const FilesGroupBody: React.FC<FilesGroupBodyProps> = ({ files, ...deps }) => {
  if (files.length === 0) return null;
  if (deps.view === 'icon') {
    return (
      <div className="pf-gallery pf-gallery--mixed">
        {files.map((f) => (
          <FileIconCard key={f.id} file={f} {...deps} />
        ))}
      </div>
    );
  }
  return (
    <div className="pf-doc-list">
      {files.map((f) => (
        <FileListRow key={f.id} file={f} {...deps} />
      ))}
    </div>
  );
};

interface FileRowProps extends RenderRowDeps {
  file: ProjectFile;
}

/** Compact "Move to folder" select shown in Project folders view. */
const FolderMoveSelect: React.FC<{
  file: ProjectFile;
  options: ProjectFolderDef[];
  onMove: (file: ProjectFile, folderPath: string | null) => void;
}> = ({ file, options, onMove }) => {
  const current = normalizeFolderPath(file.folderPath);
  return (
    <select
      className="pf-folder-move"
      value={current}
      aria-label={`Move ${file.name} to folder`}
      title="Move to folder"
      onClick={e => e.stopPropagation()}
      onChange={e => {
        const v = e.target.value;
        if (v === current) return;
        onMove(file, v === UNSORTED_FOLDER_PATH ? null : v);
      }}
    >
      <option value={UNSORTED_FOLDER_PATH}>Unsorted</option>
      {options.map(f => (
        <option key={f.path} value={f.path}>
          {f.path.includes('/') ? `  ${f.label}` : f.label}
        </option>
      ))}
    </select>
  );
};

const FileListRow: React.FC<FileRowProps> = ({
  file,
  fileHasUnreadFileComment,
  getFileCommentCount,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
  onOpenHtml,
  onDownload,
  onDelete,
  deletingId,
  mergeSelectedIds,
  onToggleMergeSelect,
  showMergeSelect,
  onMoveToFolder,
  folderMoveOptions,
  readOnly,
}) => {
  const unread = fileHasUnreadFileComment(file);
  const isPdf = isPdfType(file.mimeType);
  const mergeSelectable = showMergeSelect && isPdf && !!file.id;
  const mergeChecked = !!(file.id && mergeSelectedIds.has(file.id));
  const opensInline =
    isPdfType(file.mimeType)
    || isImageType(file.mimeType)
    || isCsvType(file.mimeType, file.name)
    || isHtmlType(file.mimeType, file.name);
  const handleOpen = () => {
    if (isPdfType(file.mimeType)) onOpenPdf(file);
    else if (isImageType(file.mimeType)) onOpenImage(file);
    else if (isCsvType(file.mimeType, file.name)) onOpenCsv(file);
    else if (isHtmlType(file.mimeType, file.name)) onOpenHtml(file);
  };
  return (
    <div className={`pf-doc-row${unread ? ' pf-doc-row--pdf-unread' : ''}${mergeChecked ? ' pf-doc-row--merge-selected' : ''}`}>
      {mergeSelectable && (
        <label className="pf-merge-check" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={mergeChecked}
            onChange={() => file.id && onToggleMergeSelect(file.id)}
            aria-label={`Select ${file.name} for merge`}
          />
        </label>
      )}
      <div className="pf-doc-icon">{fileTypeIcon(file.mimeType)}</div>
      <div className="pf-doc-info">
        {opensInline ? (
          <button type="button" className="pf-doc-name pf-doc-name-btn" onClick={handleOpen}>
            <span className="pf-doc-name-text">{file.name}</span>
          </button>
        ) : (
          <a
            className="pf-doc-name"
            href={portalHostedAssetHref(file.url, { downloadName: file.name })}
            target="_blank"
            rel="noopener noreferrer"
          >
            {file.name}
          </a>
        )}
        <span className="pf-doc-meta">
          {file.kindTag && file.kindTag !== 'none' && (
            <span className={`pf-tag-badge pf-tag-${file.kindTag}`}>
              {file.kindTag === 'timber_list'
                ? `Timber list${file.isCurrentTimberList ? '' : ' (superseded)'}`
                : file.kindTag === 'hardware_list'
                  ? `Hardware${file.isCurrentHardwareList ? '' : ' (superseded)'}`
                : 'HTML'}
            </span>
          )}
          {file.trueDimensions ? (
            <span
              className="pf-version-true-dim"
              title="True timber dimensions. Cross-section stays as written in the CSV (7.5 stays 7.5)."
            >
              True dimensions
            </span>
          ) : null}
          {file.size > 0 ? `${formatSize(file.size)} · ` : ''}
          {file.uploadedByName || 'Unknown'} · {new Date(file.uploadedAt).toLocaleDateString()}
        </span>
      </div>
      <div className="pf-doc-actions">
        <ProjectFileCommentsButton
          file={file}
          commentCount={getFileCommentCount(file)}
          hasUnread={unread}
          onOpen={handleOpen}
        />
        {onMoveToFolder && !readOnly ? (
          <FolderMoveSelect file={file} options={folderMoveOptions} onMove={onMoveToFolder} />
        ) : null}
        <button className="pf-icon-btn" onClick={() => onDownload(file)} title="Download">
          <Download size={13} />
        </button>
        {!readOnly ? (
          <button
            className="pf-icon-btn pf-icon-danger"
            onClick={() => onDelete(file)}
            disabled={deletingId === file.id}
            title="Delete"
          >
            {deletingId === file.id ? <Loader2 size={13} className="pf-spin" /> : <Trash2 size={13} />}
          </button>
        ) : null}
      </div>
    </div>
  );
};

const FileIconCard: React.FC<FileRowProps> = ({
  file,
  fileHasUnreadFileComment,
  getFileCommentCount,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
  onOpenHtml,
  onDownload,
  onDelete,
  deletingId,
  mergeSelectedIds,
  onToggleMergeSelect,
  showMergeSelect,
  onMoveToFolder,
  folderMoveOptions,
  readOnly,
}) => {
  const unread = fileHasUnreadFileComment(file);
  const isImg = isImageType(file.mimeType);
  const isPdf = isPdfType(file.mimeType);
  const mergeSelectable = showMergeSelect && isPdf && !!file.id;
  const mergeChecked = !!(file.id && mergeSelectedIds.has(file.id));
  const isCsv = isCsvType(file.mimeType, file.name);
  const isHtml = isHtmlType(file.mimeType, file.name);
  const handleOpen = () => {
    if (isPdf) onOpenPdf(file);
    else if (isImg) onOpenImage(file);
    else if (isCsv) onOpenCsv(file);
    else if (isHtml) onOpenHtml(file);
    else window.open(portalHostedAssetHref(file.url, { downloadName: file.name }), '_blank', 'noopener,noreferrer');
  };
  const openTitle = isHtml
    ? 'Open 3D model'
    : isImg || isPdf || isCsv
      ? 'Open with comments'
      : 'Open file';
  return (
    <div className={`pf-thumb-card${unread ? ' pf-doc-row--pdf-unread' : ''}${mergeChecked ? ' pf-doc-row--merge-selected' : ''}`}>
      {mergeSelectable && (
        <label className="pf-merge-check pf-merge-check--card" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={mergeChecked}
            onChange={() => file.id && onToggleMergeSelect(file.id)}
            aria-label={`Select ${file.name} for merge`}
          />
        </label>
      )}
      <button
        type="button"
        className={`pf-thumb${isImg ? '' : ' pf-thumb--icon'}`}
        onClick={handleOpen}
        title={openTitle}
      >
        {isImg ? (
          <img src={portalHostedAssetHref(file.url)} alt={file.name} loading="lazy" />
        ) : (
          <span className="pf-thumb-icon-glyph" aria-hidden>
            {fileTypeIcon(file.mimeType, 36)}
          </span>
        )}
      </button>
      <div className="pf-thumb-info">
        <span className="pf-thumb-name" title={file.name}>
          {file.name}
        </span>
        {file.kindTag && file.kindTag !== 'none' && (
          <span className={`pf-tag-badge pf-tag-${file.kindTag}`}>
            {file.kindTag === 'timber_list'
              ? 'Timber list'
              : file.kindTag === 'hardware_list'
                ? 'Hardware'
                : 'HTML'}
          </span>
        )}
        {file.trueDimensions ? (
          <span
            className="pf-version-true-dim"
            title="True timber dimensions. Cross-section stays as written in the CSV (7.5 stays 7.5)."
          >
            True dimensions
          </span>
        ) : null}
        <span className="pf-thumb-meta">
          {formatSize(file.size)} · {file.uploadedByName || 'Unknown'}
        </span>
      </div>
      <div className="pf-thumb-actions">
        <ProjectFileCommentsButton
          file={file}
          commentCount={getFileCommentCount(file)}
          hasUnread={unread}
          onOpen={handleOpen}
        />
        {onMoveToFolder && !readOnly ? (
          <FolderMoveSelect file={file} options={folderMoveOptions} onMove={onMoveToFolder} />
        ) : null}
        <button className="pf-icon-btn" onClick={() => onDownload(file)} title="Download">
          <Download size={13} />
        </button>
        {!readOnly ? (
          <button
            className="pf-icon-btn pf-icon-danger"
            onClick={() => onDelete(file)}
            disabled={deletingId === file.id}
            title="Delete"
          >
            {deletingId === file.id ? <Loader2 size={13} className="pf-spin" /> : <Trash2 size={13} />}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default ProjectFilesUnversioned;
